import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const navigateRef = useRef(navigate);
    
    // Update ref when navigate changes
    useEffect(() => {
        navigateRef.current = navigate;
    }, [navigate]);

    useEffect(() => {
        // Check if user is logged in on mount
        const userData = localStorage.getItem('user_sis');
        if (userData) {
            try {
                const parsedUser = JSON.parse(userData);
                setUser(parsedUser);
                // If we're on the login page, redirect to home
                if (window.location.hash === '#/login') {
                    navigateRef.current('/', { replace: true });
                }
            } catch (error) {
                localStorage.removeItem('user_sis');
                navigateRef.current('/login', { replace: true });
            }
        } else {
            // If no user data and not on login page, redirect to login
            if (window.location.hash !== '#/login') {
                navigateRef.current('/login', { replace: true });
            }
        }
        setLoading(false);
    }, []); // No dependencies needed

    const login = async (credentials) => {
        try {
            const response = await axios.post(
                "https://servermain.sisrevolution.com/api/schools/login/",
                credentials,
                { 
                    timeout: 15000, // Increased timeout to 15 seconds
                    validateStatus: function (status) {
                        // Don't throw error for 4xx and 5xx status codes
                        return status >= 200 && status < 600;
                    }
                }
            );
            
            if (response.status === 200) {
                const userData = response.data;
                localStorage.setItem('user_sis', JSON.stringify(userData));
                setUser(userData);
                navigate('/', { replace: true });
                return { success: true };
            }
            
            // Handle non-200 responses
            let errorMessage = 'Login failed. Please check your credentials and try again.';
            
            if (response.data) {
                if (typeof response.data === 'string') {
                    errorMessage = response.data;
                } else if (response.data.message) {
                    errorMessage = response.data.message;
                } else if (response.data.error) {
                    errorMessage = response.data.error;
                }
            }
            
            return {
                success: false,
                error: errorMessage
            };
        } catch (error) {
            let errorMessage = 'Login failed. Please try again.';
            
            // Handle network/connectivity errors
            if (error.code === 'ECONNABORTED') {
                errorMessage = 'The request took too long. Please check your internet connection and try again.';
            } else if (error.code === 'ECONNREFUSED') {
                errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
            } else if (error.code === 'ENOTFOUND' || error.code === 'EAI_AGAIN') {
                errorMessage = 'Cannot reach the server. Please check your internet connection and try again.';
            } else if (error.code === 'ETIMEDOUT' || error.code === 'ESOCKETTIMEDOUT') {
                errorMessage = 'Connection timed out. Please check your internet connection and try again.';
            } else if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
                errorMessage = 'Network error detected. Please check your internet connection and try again.';
            } else if (!error.response) {
                // No response from server - likely connectivity issue
                if (error.request) {
                    errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
                } else {
                    errorMessage = 'An unexpected error occurred. Please try again.';
                }
            } else {
                // Server responded with error
                if (error.response.data) {
                    if (typeof error.response.data === 'string') {
                        errorMessage = error.response.data;
                    } else if (error.response.data.message) {
                        errorMessage = error.response.data.message;
                    } else if (error.response.data.error) {
                        errorMessage = error.response.data.error;
                    }
                }
                
                // Handle specific status codes
                if (error.response.status === 503) {
                    errorMessage = 'The server is temporarily unavailable. Please try again in a few moments.';
                } else if (error.response.status >= 500) {
                    errorMessage = 'Server error occurred. Please try again later or contact support if the problem persists.';
                }
            }
            
            return {
                success: false,
                error: errorMessage
            };
        }
    };

    const logout = () => {
        localStorage.removeItem('user_sis');
        setUser(null);
        navigate('/login', { replace: true });
    };

    const value = {
        user,
        loading,
        login,
        logout
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}; 